import {
  AppstoreOutlined,
  ArrowDownOutlined,
  ArrowUpOutlined,
  CheckOutlined,
  ExclamationCircleOutlined,
  LockFilled,
  UnorderedListOutlined,
} from '@ant-design/icons';
import {
  ContextButton,
  IconContextButton,
  ScopeButton,
} from '@monorepo/shared-lib';
import {
  Card,
  Col,
  Divider,
  Pagination,
  Popover,
  Row,
  Segmented,
  Skeleton,
  Space,
  Switch,
  Tooltip,
} from 'antd';
import { ReactComponent as CrossIcon } from 'assets/images/icons/crossIcon.svg';
import { ReactComponent as EllipseIcon } from 'assets/images/icons/ellipsesIcon.svg';
import { ReactComponent as EyeIcon } from 'assets/images/icons/eyeIcon.svg';
import { ReactComponent as HistoryIcon } from 'assets/images/icons/historyIcon.svg';
import { ReactComponent as ToggleIcon } from 'assets/images/icons/toggleIcon.svg';
import { StyledPopconfirm } from 'components/Custom/Popconfirm';
import { BoldNameSpan, ColoredTag } from 'components/Custom/spans';
import {
  ScopeTable,
  scopeTablePageSizeSelectProps,
} from 'components/Custom/table';
import { ScopeTableFilters } from 'components/Custom/ScopeTableFilters';
import { applyFilters as applyFiltersPipeline } from 'filter-system';
import { StyledTooltip } from 'components/Custom/Tooltip';
import { UpgradeMessage } from 'containers/UpgradeMessage';
import moment from 'moment';
import { formatFromNow } from 'i18n/momentLocale';
import { AccountTypeTag } from 'components/Custom/AccountTypeTag';
import { PAGE_SIZE_USERS, USERS_VIEW_LAYOUT } from 'pages/users';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import {
  AccountType,
  PermissionType,
  SubscriptionPlan,
  UpgradeMessageSource,
} from 'store/api/enums';
import { turnModalOff, turnModalOn } from 'store/app/actions';
import { WindowType } from 'store/app/enums';
import { ModalData } from 'store/app/types';
import { User } from 'store/auth/models';
import { getAuthUserLicence } from 'store/auth/selectors';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { getBillingLoading } from 'store/billing/selectors';
import { Preference, PreferenceActions } from 'utils/preferences';
import { AvatarSizes, renderAvatar } from 'utils/render-avatar';
import { hasUnlimitedLicense } from 'utils/subscriptionPlanGate';

interface UsersComponentProps {
  loading: boolean;
  error?: string;
  members?: User[];
  currentUser?: User;
  currentSubscription?: CurrentSubscription;
  saveUserPermissions: (
    accountUuid: string,
    permissionType: PermissionType,
    enabled: boolean
  ) => void;
  openMemberWindow: () => void;
  openHistoryWindow: (userId: string, userName: string) => void;
  openRightsWindow: (accountId: string, userName: string) => void;
  deleteUser: (userToDeleteId: string) => void;
  resendActivation: (email: string) => void;
  goBack: () => void;
  getPicture: (userUuid: string) => void;
  handlePreferences: (action: PreferenceActions, preference: Preference) => any;
}

export const UsersComponent: React.FC<UsersComponentProps> = ({
  loading,
  members,
  currentUser,
  currentSubscription,
  saveUserPermissions,
  openMemberWindow,
  openHistoryWindow,
  openRightsWindow,
  deleteUser,
  resendActivation,
  goBack,
  getPicture,
  handlePreferences,
}) => {
  const dispatch = useDispatch();
  const rawPageSize = handlePreferences(PreferenceActions.GET, {
    name: PAGE_SIZE_USERS,
  });
  const pageSizeSaved =
    typeof rawPageSize === 'number' && rawPageSize > 0
      ? rawPageSize
      : typeof rawPageSize === 'string' && !Number.isNaN(Number(rawPageSize))
      ? Number(rawPageSize)
      : undefined;
  const rawViewLayout = handlePreferences(PreferenceActions.GET, {
    name: USERS_VIEW_LAYOUT,
  });
  const savedViewLayout =
    rawViewLayout === 'card' || rawViewLayout === 'list'
      ? rawViewLayout
      : undefined;
  const { t, i18n } = useTranslation();
  const userChangedViewLayoutRef = React.useRef(false);
  const [viewMode, setViewMode] = React.useState<'list' | 'card'>(() =>
    savedViewLayout === 'card' || savedViewLayout === 'list'
      ? savedViewLayout
      : 'list'
  );

  // Apply list/card from user preferences (BE via prefDetails) when they load after mount,
  // or when another tab updates preferences. Skip if the user already toggled this session
  // before prefs finished loading (avoids overwriting their choice).
  React.useEffect(() => {
    if (userChangedViewLayoutRef.current) return;
    if (savedViewLayout === 'card' || savedViewLayout === 'list') {
      setViewMode(savedViewLayout);
    }
  }, [savedViewLayout]);
  const [filtersState, setFiltersState] = React.useState<Record<string, any>>(
    {}
  );
  const [cardPage, setCardPage] = React.useState(1);
  const [permissions, setPermissions] = React.useState<any>();
  const billingLoading = useSelector(getBillingLoading);
  const userLicence = useSelector(getAuthUserLicence);

  // Check if user has access to Manager View functionality
  // Access is available for Medium, Large, and Enterprise plans, and unlimited licenses
  const hasManagerViewAccess = React.useMemo(() => {
    if (hasUnlimitedLicense(userLicence)) return true;
    // If subscription is still loading, don't show lock (return true to avoid showing lock during load)
    if (billingLoading || !currentSubscription) return true;
    // If subscription exists but no description, don't show lock (assume still loading)
    if (!currentSubscription.description) return true;
    const plan = currentSubscription.description;
    return (
      plan === SubscriptionPlan.MEDIUM_PLAN ||
      plan === SubscriptionPlan.LARGE_PLAN ||
      plan === SubscriptionPlan.ENTERPRISE_PLAN
    );
  }, [currentSubscription, billingLoading, userLicence]);

  // Handler to open upgrade modal
  const handleUpgradeClick = React.useCallback(() => {
    const modalData: ModalData = {
      title: '',
      type: WindowType.WARNING,
      component: (
        <UpgradeMessage
          source={UpgradeMessageSource.MANAGER_VIEW}
          onClose={() => dispatch(turnModalOff())}
          userAccountType={currentUser?.accountType}
        />
      ),
    };
    dispatch(turnModalOn(modalData));
  }, [dispatch, currentUser]);

  React.useEffect(() => {
    const perms = {};
    members?.map(
      (m) =>
        (perms[m.accountUuid] = {
          orgs: m.canManageOrgs,
          users: m.canManageUsers,
          manager: m.managerViewEnabled || false,
        })
    );
    setPermissions(perms);
  }, [members]);

  const pageSize = pageSizeSaved ?? 20;

  const displayMembers = React.useMemo(() => {
    const base = members ?? [];
    if (!filtersState || Object.keys(filtersState).length === 0) {
      return base;
    }
    return (
      applyFiltersPipeline(base, filtersState, { mainLocaleKey: 'users' }) ?? base
    );
  }, [members, filtersState]);

  React.useEffect(() => {
    setCardPage(1);
  }, [filtersState]);

  const cardMembers = displayMembers;

  React.useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(cardMembers.length / pageSize) || 1);
    if (cardPage > maxPage) setCardPage(maxPage);
  }, [cardMembers.length, pageSize, cardPage]);

  const paginatedCardMembers = React.useMemo(() => {
    const start = (cardPage - 1) * pageSize;
    return cardMembers.slice(start, start + pageSize);
  }, [cardMembers, cardPage, pageSize]);

  const addMember = () => {
    openMemberWindow();
  };

  const onBackClick = () => {
    goBack();
  };

  const renderRole = (_content: string, record: User) => (
    <AccountTypeTag accountTypeKey={record.accountType} t={t} />
  );

  const renderLastLogin = (_: string, record: User) => {
    if (record.accountActivated) {
      const value = formatFromNow(record.lastLogin, i18n.language);
      return <>{value}</>;
    } else {
      return <>{t('common.never')}</>;
    }
  };

  const userDisplayName = (record: User) =>
    `${record.firstName} ${record.lastName}`.trim() || record.email;

  const renderManageOrgs = (_: string, record: User) => {
    if (
      !record.isLicenseOwner &&
      AccountType[record.accountType] === AccountType.STAFF
    )
      return (
        <Tooltip
          title={
            permissions?.[record.accountUuid]?.orgs
              ? t('common.toggle-on-click-to-turn-off')
              : t('common.toggle-off-click-to-turn-on')
          }
        >
          <span
            className="users-permission-switch"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
          <Switch
            aria-label={`${t('users.manage-orgs')}: ${userDisplayName(record)}`}
            className={
              permissions && permissions[record.accountUuid]?.orgs
                ? 'switch-checked'
                : ''
            }
            checked={
              permissions ? permissions[record.accountUuid]?.orgs : false
            }
            onChange={(e) => {
              saveUserPermissions(
                record.accountUuid,
                PermissionType.ORGANISATIONS,
                e
              );

              const perm = { ...permissions };
              perm[record.accountUuid] = {
                ...perm[record.accountUuid],
                orgs: e,
              };
              setPermissions(perm);
            }}
          />
          </span>
        </Tooltip>
      );
  };

  const renderManageUsers = (_: string, record: User) => {
    if (
      !record.isLicenseOwner &&
      AccountType[record.accountType] === AccountType.STAFF
    )
      return (
        <Tooltip
          title={
            permissions?.[record.accountUuid]?.users
              ? t('common.toggle-on-click-to-turn-off')
              : t('common.toggle-off-click-to-turn-on')
          }
        >
          <span
            className="users-permission-switch"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
          <Switch
            aria-label={`${t('users.manage-users')}: ${userDisplayName(record)}`}
            className={
              permissions && permissions[record.accountUuid]?.users
                ? 'switch-checked'
                : ''
            }
            checked={
              permissions ? permissions[record.accountUuid]?.users : false
            }
            onChange={(e) => {
              saveUserPermissions(record.accountUuid, PermissionType.USERS, e);

              const perm = { ...permissions };
              perm[record.accountUuid] = {
                ...perm[record.accountUuid],
                users: e,
              };
              setPermissions(perm);
            }}
          />
          </span>
        </Tooltip>
      );
  };

  /** Card view: show Manager / Manage entities / Manage users as read-only for Client & Owner; Staff unchanged. */
  const renderManagerForCard = (record: User) => {
    const type = AccountType[record.accountType];
    if (!record.isLicenseOwner && type === AccountType.STAFF) {
      return renderManager('', record);
    }
    if (type === AccountType.CLIENT || type === AccountType.OWNER) {
      return (
        <Switch
          aria-label={`${t('other.manager')}: ${userDisplayName(record)}`}
          disabled
          checked={!!record.managerViewEnabled}
          className={record.managerViewEnabled ? 'switch-checked' : ''}
          style={{ opacity: 0.5 }}
        />
      );
    }
    return null;
  };

  const renderManageOrgsForCard = (record: User) => {
    const type = AccountType[record.accountType];
    if (!record.isLicenseOwner && type === AccountType.STAFF) {
      return renderManageOrgs('', record);
    }
    if (type === AccountType.CLIENT || type === AccountType.OWNER) {
      return (
        <Switch
          aria-label={`${t('users.manage-orgs')}: ${userDisplayName(record)}`}
          disabled
          checked={!!record.canManageOrgs}
          className={record.canManageOrgs ? 'switch-checked' : ''}
          style={{ opacity: 0.5 }}
        />
      );
    }
    return null;
  };

  const renderManageUsersForCard = (record: User) => {
    const type = AccountType[record.accountType];
    if (!record.isLicenseOwner && type === AccountType.STAFF) {
      return renderManageUsers('', record);
    }
    if (type === AccountType.CLIENT || type === AccountType.OWNER) {
      return (
        <Switch
          aria-label={`${t('users.manage-users')}: ${userDisplayName(record)}`}
          disabled
          checked={!!record.canManageUsers}
          className={record.canManageUsers ? 'switch-checked' : ''}
          style={{ opacity: 0.5 }}
        />
      );
    }
    return null;
  };

  const renderManager = (_: string, record: User) => {
    if (
      !record.isLicenseOwner &&
      AccountType[record.accountType] === AccountType.STAFF
    ) {
      if (!hasManagerViewAccess) {
        return (
          <Space>
            <Tooltip title={t('common.toggle-off-click-to-turn-on')}>
              <span>
                <Switch
                  aria-label={`${t('other.manager')}: ${userDisplayName(record)}`}
                  disabled
                  checked={
                    permissions
                      ? permissions[record.accountUuid]?.manager
                      : false
                  }
                  style={{ opacity: 0.5 }}
                />
              </span>
            </Tooltip>
            <LockFilled
              style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: '12px', cursor: 'pointer' }}
              onClick={handleUpgradeClick}
            />
          </Space>
        );
      }
      return (
        <Tooltip
          title={
            permissions?.[record.accountUuid]?.manager
              ? t('common.toggle-on-click-to-turn-off')
              : t('common.toggle-off-click-to-turn-on')
          }
        >
          <span
            className="users-permission-switch"
            onClick={(event) => event.stopPropagation()}
            onMouseDown={(event) => event.stopPropagation()}
          >
          <Switch
            aria-label={`${t('other.manager')}: ${userDisplayName(record)}`}
            className={
              permissions && permissions[record.accountUuid]?.manager
                ? 'switch-checked'
                : ''
            }
            checked={
              permissions ? permissions[record.accountUuid]?.manager : false
            }
            onChange={(e) => {
              saveUserPermissions(
                record.accountUuid,
                PermissionType.MANAGER,
                e
              );

              const perm = { ...permissions };
              perm[record.accountUuid] = {
                ...perm[record.accountUuid],
                manager: e,
              };
              setPermissions(perm);
            }}
          />
          </span>
        </Tooltip>
      );
    }
  };

  const renderContextMenu = (record: User) => {
    // const to = `/entities/organisation/${record.organisationId}`;
    const profile = `/profile/${record.accountUuid}`;
    return (
      <div
       className="context-menu-items">
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <IconContextButton
            type="text"
            icon={
              <EyeIcon
                style={{ width: '18px', height: '22px', marginRight: '4px' }}
              />
            }
          >
            <Link style={{ textDecoration: 'none' }} to={profile}>
              {t('users.view-details')}
            </Link>
          </IconContextButton>
          {record.accountActivated && record.lastLogin && (
            <IconContextButton
              type="text"
              onClick={() => {
                openHistoryWindow(
                  record.userUuid,
                  record.firstName + ' ' + record.lastName
                );
              }}
              icon={
                <HistoryIcon
                  style={{
                    width: '18px',
                    height: '20px',
                    marginRight: '5px',
                  }}
                />
              }
            >
              {t('users.login-history')}
            </IconContextButton>
          )}

          <>
            {currentUser?.canManageUsers &&
              AccountType[record.accountType] !== AccountType.OWNER && (
              <IconContextButton
                type="text"
                onClick={() =>
                  openRightsWindow(
                    record.accountUuid,
                    record.firstName + ' ' + record.lastName
                  )
                }
                icon={
                  <ToggleIcon
                    style={{
                      width: '20px',
                      height: '20px',
                      marginRight: '3px',
                    }}
                  />
                }
              >
                {t('users.access-rights')}
              </IconContextButton>
            )}
            {currentUser?.canManageUsers &&
              AccountType[record.accountType] !== AccountType.OWNER && (
              <StyledPopconfirm // @ts-ignore
                title={t('users.delete-user-title')}
                description={
                  <span>
                    {t('users.delete-user-description').split('{br}')[0]}
                    <br />
                    {t('users.delete-user-description').split('{br}')[1]}
                  </span>
                }
                onConfirm={() => onDeleteUser(record.userUuid)}
                placement="left"
                okText={t('common.yes')}
                cancelText={t('common.cancel')}
              >
                <IconContextButton
                  type="text"
                  icon={
                    <CrossIcon
                      style={{
                        width: '10px',
                        height: '22px',
                        marginRight: '8px',
                        marginLeft: '5px',
                      }}
                    />
                  }
                >
                  {t('users.delete-user')}
                </IconContextButton>
              </StyledPopconfirm>
            )}
          </>
        </Space>
      </div>
    );
  };

  const renderButtons = (_: string, record: User) => {
    if (
      AccountType[record.accountType] === AccountType.OWNER &&
      currentUser?.userUuid !== record.userUuid
    )
      return;
    const hideAccessRights =
      AccountType[record.accountType] === AccountType.OWNER;
    return (
      <Space size={8} align="center" style={{ flexShrink: 0 }}>
        {currentUser?.canManageUsers && (
          <ScopeButton //@ts-ignore
            type="default"
            width="auto"
            height="26px"
            fontSize="11px"
            lineheight="12px"
            aria-hidden={hideAccessRights || undefined}
            tabIndex={hideAccessRights ? -1 : 0}
            onClick={(e) => {
              e?.stopPropagation?.();
              if (hideAccessRights) return;
              openRightsWindow(
                record.accountUuid,
                record.firstName + ' ' + record.lastName
              );
            }}
            style={{
              whiteSpace: 'nowrap',
              paddingInline: 10,
              visibility: hideAccessRights ? 'hidden' : 'visible',
            }}
          >
            {t('users.access-rights')}
          </ScopeButton>
        )}
        <Popover
          placement="topLeft"
          arrow={false}
          trigger="click"
          content={renderContextMenu(record)}
          title={''}
          overlayStyle={{ zIndex: 999 }}
        >
          <ContextButton
            type="text"
            aria-label={t('common.more-actions')}
            onClick={(e) => e?.stopPropagation?.()}
          >
            <EllipseIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </ContextButton>
        </Popover>
      </Space>
    );
  };

  const onDeleteUser = (userToDelete: string) => {
    deleteUser(userToDelete);
  };

  const renderUserColumns = (_, record: User) => {
    const profile = `/profile/${record.accountUuid}`;
    return (
      <Space>
        {renderAvatar(
          record.firstName,
          record.lastName,
          AvatarSizes.DEFAULT,
          record.userUuid,
          record?.profilePicture
        )}
        <Space direction="vertical" style={{ rowGap: '0' }}>
          {AccountType[record.accountType] === AccountType.OWNER &&
          currentUser?.userUuid !== record.userUuid ? (
            <BoldNameSpan>{`${record.firstName} ${record.lastName}`}</BoldNameSpan>
          ) : (
            <Link
              to={profile}
              style={{ textDecoration: 'none', color: 'inherit' }}
            >
              <BoldNameSpan>{`${record.firstName} ${record.lastName}`}</BoldNameSpan>
            </Link>
          )}
          <BoldNameSpan //@ts-ignore
            fontWeight={400}
          >
            {record.email}
          </BoldNameSpan>
        </Space>
      </Space>
    );
  };

  const renderUserCardIdentity = (record: User) => {
    const profile = `/profile/${record.accountUuid}`;
    const nameStyle: React.CSSProperties = {
      fontSize: 16,
      fontWeight: 600,
      lineHeight: '22px',
      display: 'block',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };
    const nameNode = (
      <BoldNameSpan style={nameStyle}>
        {`${record.firstName} ${record.lastName}`}
      </BoldNameSpan>
    );

    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: 14,
          minWidth: 0,
          width: '100%',
        }}
      >
        <div style={{ flexShrink: 0 }}>
          {renderAvatar(
            record.firstName,
            record.lastName,
            AvatarSizes.LARGE,
            record.userUuid,
            record?.profilePicture
          )}
        </div>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            display: 'flex',
            flexDirection: 'column',
            rowGap: 4,
          }}
        >
          {AccountType[record.accountType] === AccountType.OWNER &&
          currentUser?.userUuid !== record.userUuid ? (
            nameNode
          ) : (
            <Link
              to={profile}
              style={{
                textDecoration: 'none',
                color: 'inherit',
                minWidth: 0,
                overflow: 'hidden',
              }}
            >
              {nameNode}
            </Link>
          )}
          <BoldNameSpan //@ts-ignore
            fontWeight={400}
            style={{
              fontSize: 13,
              lineHeight: '18px',
              color: 'var(--color-text-muted, #5a6a6a)',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {record.email}
          </BoldNameSpan>
          <Space
            size={[6, 6]}
            wrap
            align="center"
            style={{ marginTop: 6, overflow: 'visible' }}
          >
            {renderRole(record.accountType, record)}
            {renderStatus('', record)}
          </Space>
        </div>
      </div>
    );
  };

  const renderCreatedUser = (_, record: User) => {
    if (
      AccountType[record.accountType] === AccountType.OWNER ||
      !record.accountCreatorName?.trim()
    ) {
      return null;
    }
    const creatorNames = record.accountCreatorName.trim().split(' ');
    return (
      <Space>
        {renderAvatar(
          creatorNames[0],
          creatorNames[1] || '',
          AvatarSizes.DEFAULT
        )}
        <Space direction="vertical" style={{ rowGap: '0' }}>
          <BoldNameSpan //@ts-ignore
            fontWeight={400}
          >
            {record.accountCreatorName}
          </BoldNameSpan>
        </Space>
      </Space>
    );
  };

  const renderStatus = (content: string, record: User) => {
    if (record.accountActivated) {
      return (
        <ColoredTag
          color="var(--color-status-success, #058181)" //@ts-ignore
          bgcolor="var(--color-status-success-bg, #E9F6F7)"
          bordered={true}
          bordercolor="#DBEAE3"
          icon={<CheckOutlined />}
        >
          {t('common.active')}
        </ColoredTag>
      );
    } else {
      return (
        <ScopeButton //@ts-ignore
          width="113px"
          height="25px"
          fontSize="12px"
          lineheight="12px"
          onClick={() => resendActivation(record.email)}
          type="primary"
          style={{ flexShrink: 0 }}
        >
          {t('users.resend-email')}
        </ScopeButton>
      );
    }
  };

  const renderSortIcon = ({ sortOrder }) => {
    if (sortOrder == 'ascend') {
      return <ArrowDownOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    } else {
      return <ArrowUpOutlined style={{ color: 'var(--color-text-muted, #5a6a6a)' }} />;
    }
  };

  const getManageTitle = (isOrg) => {
    return (
      <>
        {t('users.manage-tooltip-intro')}
        {isOrg ? (
          <ul>
            <li>{t('users.manage-orgs-item-1')}</li>
            <li>{t('users.manage-orgs-item-2')}</li>
            <li>{t('users.manage-orgs-item-3')}</li>
            <li>{t('users.manage-orgs-item-4')}</li>
          </ul>
        ) : (
          <ul>
            <li>{t('users.manage-users-item-1')}</li>
            <li>{t('users.manage-users-item-2')}</li>
            <li>{t('users.manage-users-item-3')}</li>
            <li>{t('users.manage-users-item-4')}</li>
          </ul>
        )}
      </>
    );
  };

  const renderManageOrgsOrUsersTitle = (isOrg) => {
    return (
      <>
        {isOrg ? t('users.manage-orgs') : t('users.manage-users')}
        {'   '}
        <StyledTooltip // @ts-ignore
          title={getManageTitle(isOrg)}
          placement="top"
          arrow={false}
          width="395px"
          trigger={['hover', 'click']}
        >
          <ExclamationCircleOutlined />
        </StyledTooltip>
      </>
    );
  };

  const renderManagerTitle = () => {
    return (
      <>
        <Space>
          {!hasManagerViewAccess ? (
            <StyledTooltip // @ts-ignore
              title={t('dashboard.unlock-manager-view-tooltip')}
              placement="bottom"
              arrow={false}
            >
              <Space style={{ cursor: 'pointer' }}>
                {t('other.manager')}
                <LockFilled
                  style={{ color: 'var(--color-text-muted, #5a6a6a)', fontSize: '12px' }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleUpgradeClick();
                  }}
                />
              </Space>
            </StyledTooltip>
          ) : (
            t('other.manager')
          )}
          <StyledTooltip // @ts-ignore
            title={t('other.manager-view-description')}
            placement="top"
            arrow={false}
            width="395px"
            trigger={['hover', 'click']}
          >
            <ExclamationCircleOutlined />
          </StyledTooltip>
        </Space>
      </>
    );
  };

  const columns = [
    {
      title: t('users.user'),
      dataIndex: 'fullName',
      key: 'fullName',
      render: renderUserColumns,
      sorter: (a, b) => a.firstName?.localeCompare(b.firstName),
      sortIcon: renderSortIcon,
    },
    {
      title: t('users.accountType'),
      dataIndex: 'accountType',
      key: 'accountType',
      render: renderRole,
      sorter: (a, b) =>
        AccountType[a.accountType].localeCompare(AccountType[b.accountType]),
      sortIcon: renderSortIcon,
    },
    {
      title: t('users.last-login'),
      dataIndex: 'lastLogin ',
      key: 'lastLogin',
      render: renderLastLogin,
      sorter: (a, b) =>
        new Date(a.lastLogin).valueOf() - new Date(b.lastLogin).valueOf(),
      sortIcon: renderSortIcon,
    },
    {
      title: t('organisation.created-by'),
      dataIndex: 'accountCreatorName',
      key: 'accountCreatorName',
      render: renderCreatedUser,
      sorter: (a, b) =>
        a.accountCreatorName?.localeCompare(b.accountCreatorName),
      sortIcon: renderSortIcon,
    },
    {
      title: t('main.status'),
      dataIndex: 'accountActivated',
      key: 'accountActivated',
      render: renderStatus,
    },
    {
      title: renderManagerTitle(),
      dataIndex: 'managerViewEnabled',
      key: 'managerViewEnabled',
      render: (c, r: User) =>
        currentUser?.userUuid !== r.userUuid && renderManager(c, r),
    },
    {
      title: renderManageOrgsOrUsersTitle(true),
      dataIndex: 'canManageOrgs',
      key: 'canManageOrgs',
      render: (c, r: User) =>
        currentUser?.userUuid !== r.userUuid && renderManageOrgs(c, r),
    },
    {
      title: renderManageOrgsOrUsersTitle(false),
      dataIndex: 'canManageUsers',
      key: 'canManageUsers',
      render: (c, r: User) =>
        currentUser?.userUuid !== r.userUuid && renderManageUsers(c, r),
    },
    {
      render: renderButtons,
    },
  ];

  const renderCardFieldRow = (
    label: React.ReactNode,
    content: React.ReactNode | null | undefined
  ) => {
    if (content === null || content === undefined) return null;
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 10,
          padding: '6px 0',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            color: 'var(--color-text-muted, #5a6a6a)',
            fontSize: 12,
            fontWeight: 400,
            lineHeight: '18px',
            fontFamily: 'Inter',
          }}
        >
          {label}
        </span>
        <div
          style={{
            flex: 1,
            minWidth: 0,
            textAlign: 'right',
            fontSize: 14,
            fontWeight: 400,
            lineHeight: '20px',
            color: '#042a2a',
            fontFamily: 'Inter',
          }}
        >
          {content}
        </div>
      </div>
    );
  };

  const renderAddUserButton = () => {
    return (
      <Space wrap size={10} align="center">
        <Segmented
          value={viewMode}
          onChange={(v) => {
            const next = v as 'list' | 'card';
            userChangedViewLayoutRef.current = true;
            setViewMode(next);
            handlePreferences(PreferenceActions.SET, {
              name: USERS_VIEW_LAYOUT,
              value: next,
            });
          }}
          options={[
            {
              label: t('dashboard.view-list'),
              value: 'list',
              icon: <UnorderedListOutlined />,
            },
            {
              label: t('users.view-card'),
              value: 'card',
              icon: <AppstoreOutlined />,
            },
          ]}
        />
        {currentUser?.canManageUsers && (
          <ScopeButton //@ts-ignore
            width="117px"
            height="32px"
            fontSize="13px"
            lineheight="12px"
            onClick={addMember}
            type="primary"
          >
            {t('users.add-user')}
          </ScopeButton>
        )}
      </Space>
    );
  };

  const savePageSize = (newPageSize: number) => {
    handlePreferences(PreferenceActions.SET, {
      name: PAGE_SIZE_USERS,
      value: newPageSize,
    });
    setCardPage(1);
  };

  const [hasScrollbar, setHasScrollbar] = React.useState(false);

  React.useEffect(() => {
    const checkScrollbar = () => {
      // Use setTimeout to ensure DOM is fully rendered
      setTimeout(() => {
        const hasVerticalScrollbar =
          document.documentElement.scrollHeight >
          document.documentElement.clientHeight;
        const scrollbarWidth =
          window.innerWidth - document.documentElement.clientWidth;
        setHasScrollbar(hasVerticalScrollbar && scrollbarWidth > 0);
      }, 100);
    };

    checkScrollbar();
    window.addEventListener('resize', checkScrollbar);
    window.addEventListener('scroll', checkScrollbar);
    const resizeObserver = new ResizeObserver(checkScrollbar);
    if (document.body) {
      resizeObserver.observe(document.body);
    }

    // Also check after a delay to catch late-rendered content
    const timeoutId = setTimeout(checkScrollbar, 500);

    return () => {
      window.removeEventListener('resize', checkScrollbar);
      window.removeEventListener('scroll', checkScrollbar);
      resizeObserver.disconnect();
      clearTimeout(timeoutId);
    };
  }, [members, loading]);

  return (
    <div
      style={{
        padding: '40px',
        paddingTop: '10px',
        maxWidth: hasScrollbar ? 1662 : 1678,
        minWidth: 1200,
        margin: 'auto',
      }}
    >
      {loading ? (
        viewMode === 'card' ? (
          <div
            style={{
              padding: '24px',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #dbeae3',
            }}
          >
            <div
              style={{
                marginBottom: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <Skeleton
                active
                paragraph={{ rows: 0 }}
                title={{ width: '120px' }}
              />
              <Space wrap>
                <Skeleton.Button active style={{ width: 180, height: 32 }} />
                <Skeleton.Button active style={{ width: 117, height: 32 }} />
              </Space>
            </div>
            <div
              style={{
                display: 'flex',
                gap: '12px',
                marginBottom: '20px',
                flexWrap: 'wrap',
                paddingBottom: '12px',
                borderBottom: '1px solid #f0f0f0',
              }}
            >
              {[1, 2, 3].map((i) => (
                <Skeleton.Input
                  key={i}
                  active
                  style={{ width: 140, height: 32 }}
                />
              ))}
              <Skeleton.Button active style={{ width: 90, height: 24 }} />
            </div>
            <Row gutter={[16, 16]}>
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Col xs={24} sm={12} lg={8} xl={6} key={i}>
                  <div
                    style={{
                      borderRadius: 10,
                      border: '1px solid #dbeae3',
                      background: '#ffffff',
                      boxShadow: '0px 0px 10px 0px #0000000d',
                      padding: 24,
                      height: '100%',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'flex-start',
                        gap: 12,
                      }}
                    >
                      <Space
                        align="start"
                        size={14}
                        style={{ flex: 1, minWidth: 0 }}
                      >
                        <Skeleton.Avatar active size={64} shape="circle" />
                        <Space
                          direction="vertical"
                          size={0}
                          style={{ rowGap: 4, flex: 1, minWidth: 0 }}
                        >
                          <Skeleton.Input
                            active
                            style={{ width: 'min(100%, 200px)', height: 18 }}
                          />
                          <Skeleton.Input
                            active
                            style={{ width: 'min(100%, 240px)', height: 14 }}
                          />
                          <Space size={[6, 6]} wrap style={{ marginTop: 6 }}>
                            <Skeleton.Button
                              active
                              style={{ width: 68, height: 24 }}
                            />
                            <Skeleton.Button
                              active
                              style={{ width: 76, height: 24 }}
                            />
                          </Space>
                        </Space>
                      </Space>
                      <Space size={8} style={{ flexShrink: 0 }}>
                        <Skeleton.Button
                          active
                          style={{ width: 100, height: 26 }}
                        />
                        <Skeleton.Button
                          active
                          size="small"
                          style={{ width: 28, height: 28 }}
                        />
                      </Space>
                    </div>
                    <div
                      style={{
                        height: 1,
                        background: '#dbeae3',
                        margin: '20px 0 14px',
                      }}
                    />
                    {[1, 2].map((row) => (
                      <div
                        key={row}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 10,
                          padding: '6px 0',
                        }}
                      >
                        <Skeleton.Input
                          active
                          style={{ width: row === 1 ? 72 : 78, height: 12 }}
                        />
                        <Skeleton.Input
                          active
                          style={{
                            width: row === 1 ? 120 : 168,
                            height: row === 1 ? 20 : 22,
                          }}
                        />
                      </div>
                    ))}
                    <div
                      style={{
                        height: 1,
                        background: '#dbeae3',
                        margin: '14px 0 12px',
                      }}
                    />
                    <div
                      style={{
                        background: '#f9fbfc',
                        borderRadius: 8,
                        border: '1px solid #ecf2f3',
                        padding: '10px 14px',
                      }}
                    >
                      {[1, 2, 3].map((row) => (
                        <div
                          key={row}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 10,
                            padding: '6px 0',
                          }}
                        >
                          <Skeleton.Input
                            active
                            style={{ width: row === 1 ? 88 : 124, height: 12 }}
                          />
                          <Skeleton.Input
                            active
                            style={{ width: 40, height: 22 }}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
            <div
              style={{
                marginTop: 24,
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Skeleton.Input active style={{ width: 320, height: 32 }} />
            </div>
          </div>
        ) : (
          <div
            style={{
              padding: '24px',
              background: '#fff',
              borderRadius: '4px',
              border: '1px solid #dbeae3',
            }}
          >
            <div
              style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
              }}
            >
              <Skeleton.Button
                active
                size="default"
                style={{ width: '120px' }}
              />
            </div>
            <Skeleton
              active
              paragraph={{ rows: 0 }}
              title={{ width: '200px' }}
            />
            <div style={{ marginTop: '24px' }}>
              {/* Table Header Skeleton */}
              <div
                style={{
                  display: 'flex',
                  gap: '16px',
                  marginBottom: '16px',
                  paddingBottom: '16px',
                  borderBottom: '1px solid #f0f0f0',
                }}
              >
                {[1, 2, 3, 4, 5, 6].map((i) => (
                  <Skeleton.Input
                    key={i}
                    active
                    size="default"
                    style={{ flex: 1, height: '32px' }}
                  />
                ))}
              </div>
              {/* Table Rows Skeleton */}
              <Skeleton active paragraph={{ rows: 10 }} />
            </div>
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <Skeleton.Input
                active
                size="default"
                style={{ width: '200px' }}
              />
              <div style={{ display: 'flex', gap: '8px' }}>
                <Skeleton.Button
                  active
                  size="default"
                  style={{ width: '32px' }}
                />
                <Skeleton.Button
                  active
                  size="default"
                  style={{ width: '32px' }}
                />
                <Skeleton.Button
                  active
                  size="default"
                  style={{ width: '100px' }}
                />
              </div>
            </div>
          </div>
        )
      ) : (
        <Row justify="center" gutter={[0, 16]}>
          <Col span={24}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8,
                marginBottom: 3,
              }}
            >
            
                <ScopeTableFilters
                  filters={[
                    'accountType',
                    'accountActivated',
                    'accountCreatorName',
                  ]}
                  mainLocaleKey="users"
                  value={filtersState}
                  onChange={setFiltersState}
                  originalDataSource={members}
                  tableDataSource={displayMembers}
                  embedded={false}
                  filterRowMarginBottom={3}
                />
              
              <div
                style={{
                  flexShrink: 0,
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 3,
                }}
              >
                {renderAddUserButton()}
              </div>
            </div>
            <div
              className={
                viewMode === 'card' ? 'users-scope-table-card-mode' : undefined
              }
            >
              <style>{`
                .users-scope-table-card-mode .ant-table-wrapper,
                .users-scope-table-card-mode .ant-pagination {
                  display: none !important;
                }
              `}</style>
              <ScopeTable //@ts-ignore
                rowKey="accountUuid"
                columns={columns}
                defaultSort={['fullName', 'asc']}
                originalDataSource={members} //@ts-ignore
                tableDataSource={displayMembers} //@ts-ignore
                pagination={true}
                rowsPerPage={pageSize}
                enableColumnsCustomization={false}
                rowHeight={60} //@ts-ignore
                haveBorder
                showSizeChanger
                getNewPageSize={savePageSize}
                transactionType=""
                reportId=""
              />
            </div>
            {viewMode === 'card' && (
              <>
                <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                  {paginatedCardMembers.map((record) => {
                    const cardManagerControl = renderManagerForCard(record);
                    const cardManageOrgsControl =
                      renderManageOrgsForCard(record);
                    const cardManageUsersControl =
                      renderManageUsersForCard(record);
                    const hasPermissionsSection =
                      cardManagerControl != null ||
                      cardManageOrgsControl != null ||
                      cardManageUsersControl != null;

                    return (
                      <Col
                        xs={24}
                        sm={12}
                        lg={8}
                        xl={6}
                        key={record.accountUuid}
                      >
                        <Card
                          style={{
                            borderRadius: 10,
                            border: '1px solid #dbeae3',
                            height: '100%',
                            background: '#ffffff',
                            boxShadow: '0px 0px 10px 0px #0000000d',
                          }}
                          styles={{ body: { padding: 24 } }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'flex-start',
                              gap: 12,
                            }}
                          >
                            <div
                              style={{
                                flex: 1,
                                minWidth: 0,
                                overflow: record.accountActivated
                                  ? 'hidden'
                                  : 'visible',
                              }}
                            >
                              {renderUserCardIdentity(record)}
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              {renderButtons('', record)}
                            </div>
                          </div>
                          <Divider
                            style={{
                              margin: '20px 0 14px',
                              borderColor: '#dbeae3',
                            }}
                          />
                          {renderCardFieldRow(
                            t('users.last-login'),
                            renderLastLogin('', record)
                          )}
                          {renderCardFieldRow(
                            t('organisation.created-by'),
                            renderCreatedUser('', record)
                          )}
                          {hasPermissionsSection && (
                            <>
                              <Divider
                                style={{
                                  margin: '14px 0 12px',
                                  borderColor: '#dbeae3',
                                }}
                              />
                              <div
                                style={{
                                  background: '#f9fbfc',
                                  borderRadius: 8,
                                  border: '1px solid #ecf2f3',
                                  padding: '10px 14px',
                                }}
                              >
                                {renderCardFieldRow(
                                  renderManagerTitle(),
                                  cardManagerControl
                                )}
                                {renderCardFieldRow(
                                  renderManageOrgsOrUsersTitle(true),
                                  cardManageOrgsControl
                                )}
                                {renderCardFieldRow(
                                  renderManageOrgsOrUsersTitle(false),
                                  cardManageUsersControl
                                )}
                              </div>
                            </>
                          )}
                        </Card>
                      </Col>
                    );
                  })}
                </Row>
                {cardMembers.length > 0 && (
                  <Pagination
                    style={{
                      marginTop: 24,
                      display: 'flex',
                      justifyContent: 'flex-end',
                    }}
                    current={cardPage}
                    pageSize={pageSize}
                    total={cardMembers.length}
                    onChange={(p) => setCardPage(p)}
                    showSizeChanger={scopeTablePageSizeSelectProps}
                    pageSizeOptions={['10', '20', '50', '100']}
                    onShowSizeChange={(_, size) => savePageSize(size)}
                  />
                )}
              </>
            )}
          </Col>
        </Row>
      )}
    </div>
  );
};
